import {
	createRouter,
	createWebHistory,
	type EditFormRouteMeta,
	type IndexFormRouteMeta,
	type LinkRouteMeta,
	type NavigationGuardNext,
	type RouteLocationNormalized,
	type RouteParams
} from 'vue-router'

import type { ISalesperson } from '@/interfaces'
import { AccessLevel, useAuthStore, useQuoteStore } from '@/stores'

import { VueNavigationClient } from './helpers'

// List of routes without login protection
const unguarded = ['/', '/login', '/logout']

export const routes = [
	{
		path: '/',
		name: 'home',
		component: () => import('@/views/HomeView.vue')
	},
	{
		path: '/user',
		name: 'User',
		component: () => import('@/views/UserView.vue')
	},
	{
		path: '/login',
		name: 'Login',
		component: { render: () => null },
		beforeEnter: async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
			const result = await useAuthStore().login()
			if (result) {
				next()
			} else {
				next(false)
			}
		}
	},
	{
		path: '/logout',
		name: 'Logout',
		component: { render: () => null },
		beforeEnter: async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
			await useAuthStore().logout()
			next()
		}
	},
	{
		path: '/:pathMatch(.*)*',
		name: '404',
		component: () => import('@/views/404View.vue')
	},
	{
		path: '/email',
		name: 'email',
		component: () => import('@/views/EmailView.vue')
	},
	{
		path: '/leads',
		meta: {
			linkLabel: 'Leads',
			linkNavBar: () => {
				return useAuthStore().hasAnyDomainAccess('Client').value
			},
			linkNavOrder: 0
		} as LinkRouteMeta,
		children: [
			{
				path: '',
				name: 'leadIndex',
				component: () => import('@/components/client/ClientIndex.vue'),
				meta: {
					filters: {
						type: 'Lead',
						salesPerson: (salesPerson: string) => {
							return (
								useAuthStore().mySalesTeams.find((sp: ISalesperson) => sp.name === salesPerson) !==
									undefined ||
								useAuthStore().getAccessForModuleAndRole('Client', 'Read').value >= AccessLevel.Full
							)
						}
					},
					addRoute: 'leadAdd',
					editRoute: 'leadEdit'
				} as IndexFormRouteMeta
			},
			{
				path: 'edit/:clientId',
				name: 'leadEdit',
				component: () => import('@/components/client/ClientEdit.vue'),
				meta: {
					formMode: 'Edit',
					submitRoute: 'leadEdit',
					exitRoute: 'leadIndex'
				} as EditFormRouteMeta,
				props: true
			},
			{
				path: 'add',
				name: 'leadAdd',
				component: () => import('@/components/client/ClientEdit.vue'),
				meta: {
					formMode: 'Add',
					submitRoute: 'leadEdit',
					exitRoute: 'leadIndex'
				} as EditFormRouteMeta
			}
		]
	},
	{
		path: '/clients',
		meta: {
			linkLabel: 'Clients',
			linkNavBar: () => {
				return useAuthStore().hasAnyDomainAccess('Client').value
			},
			linkNavOrder: 1
		} as LinkRouteMeta,
		children: [
			{
				path: '',
				name: 'clientIndex',
				component: () => import('@/components/client/ClientIndex.vue'),
				meta: {
					filters: {
						type: 'Client',
						row: () => {
							return useAuthStore().getAccessForModuleAndRole('Client', 'Read').value >= AccessLevel.Basic
						}
					},
					addRoute: 'clientAdd',
					editRoute: 'clientEdit'
				} as IndexFormRouteMeta
			},
			{
				path: 'edit/:clientId',
				name: 'clientEdit',
				component: () => import('@/components/client/ClientEdit.vue'),
				meta: {
					formMode: 'Edit',
					submitRoute: 'clientEdit',
					exitRoute: 'clientIndex'
				} as EditFormRouteMeta,
				props: true
			},
			{
				path: 'add',
				name: 'clientAdd',
				redirect: { name: 'leadAdd' }
			}
		]
	},
	{
		path: '/shows',
		name: 'shows',
		meta: {
			linkLabel: 'Shows',
			linkNavBar: () => {
				return useAuthStore().hasAnyDomainAccess('Event').value
			},
			linkNavOrder: 2
		} as LinkRouteMeta,
		children: [
			{
				path: '',
				name: 'eventIndex',
				component: () => import('@/views/EventsView.vue'),
				meta: {
					filters: {
						row: () => {
							return useAuthStore().getAccessForModuleAndRole('Event', 'Read').value >= AccessLevel.Basic
						}
					},
					addRoute: 'eventAdd',
					editRoute: 'eventEdit'
				} as IndexFormRouteMeta
			},
			{
				path: 'edit/:eventId',
				name: 'eventEdit',
				component: () => import('@/components/event/EventEdit.vue'),
				meta: {
					formMode: 'Edit',
					submitRoute: 'eventEdit',
					exitRoute: 'eventIndex'
				} as EditFormRouteMeta,
				props: true
			},
			{
				path: 'edit/0',
				redirect: { name: 'eventAdd' }
			},
			{
				path: 'add',
				name: 'eventAdd',
				component: () => import('@/components/event/EventEdit.vue'),
				meta: {
					formMode: 'Add',
					submitRoute: 'eventEdit',
					exitRoute: 'eventIndex'
				} as EditFormRouteMeta
			}
		]
	},
	{
		path: '/jobs',
		name: 'jobs',
		meta: {
			linkLabel: 'Jobs',
			linkNavBar: () => {
				return useAuthStore().hasAnyDomainAccess('Job').value
			},
			linkNavOrder: 3
		} as LinkRouteMeta,
		children: [
			{
				path: '',
				name: 'jobIndex',
				component: () => import('@/components/job/JobIndex.vue'),
				meta: {
					filters: {
						salesTeam: (salesTeam: number) => {
							return (
								useAuthStore().mySalesTeams.find((sp: ISalesperson) => sp.id === salesTeam) !==
									undefined ||
								useAuthStore().getAccessForModuleAndRole('Job', 'Read').value >= AccessLevel.Basic
							)
						},
						row: () => {
							return useAuthStore().getAccessForModuleAndRole('Job', 'Read').value >= AccessLevel.Basic
						}
					},
					// Example of default filters - these column filters will be applied when the page loads
					// defaultFilters: {
					// 	status: ['Active', 'Pending'],  // For dropdown/multiselect columns
					// 	timeline: 'Current',            // For text columns
					// 	totalRevenue: { min: 100000, max: null }  // For number/currency columns
					// },
					// // Example of default column visibility - these columns will be hidden when the page loads
					// defaultColumnVisibility: ['clientId', 'startDate'], // Hide clientId and startDate columns by default
					addRoute: 'jobAdd',
					editRoute: 'jobEdit'
				} as IndexFormRouteMeta
			},
			{
				path: 'edit/:jobId',
				name: 'jobEdit',
				component: () => import('@/components/job/JobEdit.vue'),
				meta: {
					formMode: 'Edit',
					submitRoute: 'jobEdit',
					exitRoute: 'jobIndex'
				} as EditFormRouteMeta,
				props: true
			},
			{
				path: 'add',
				name: 'jobAdd',
				component: () => import('@/components/job/JobEdit.vue'),
				meta: {
					formMode: 'Add',
					submitRoute: 'jobEdit',
					exitRoute: 'jobIndex'
				} as EditFormRouteMeta
			},
			{
				path: ':jobId/subJobAdd/',
				name: 'subJobAdd',
				component: () => import('@/components/subJob/SubJobEdit.vue'),
				props: true,
				meta: {
					formMode: 'Add',
					submitRoute: 'jobEdit'
				} as EditFormRouteMeta
			}
		]
	},
	{
		path: '/subJob',
		name: 'subJobIndex',
		component: () => import('@/views/SubJobsView.vue'),
		meta: {
			linkLabel: 'SubJob',
			linkNavBar: () => {
				return useAuthStore().hasAnyDomainAccess('Job').value
			},
			linkNavOrder: 4,
			filters: {
				salesTeam: (salesTeam: number) => {
					return (
						useAuthStore().mySalesTeams.find((sp: ISalesperson) => sp.id === salesTeam) !== undefined ||
						useAuthStore().getAccessForModuleAndRole('Job', 'Read').value >= AccessLevel.Full
					)
				},
				row: () => {
					return useAuthStore().getAccessForModuleAndRole('Job', 'Read').value >= AccessLevel.Basic
				}
			},
			addRoute: '', // SubJob creation requires a parent Job context
			editRoute: 'subJobEdit'
		} as LinkRouteMeta & IndexFormRouteMeta,
		children: [
			{
				path: 'edit/:subJobId/forecast',
				name: 'subJobEditForecast',
				component: () => import('@/components/subJob/SubJobEdit.vue'),
				meta: {
					formMode: 'Edit',
					submitRoute: 'forecastIndex',
					exitRoute: 'forecastIndex'
				} as EditFormRouteMeta,
				props: true
			},
			{
				path: 'edit/:subJobId/capacity',
				name: 'subJobEditCapacity',
				component: () => import('@/components/subJob/SubJobEdit.vue'),
				meta: {
					formMode: 'Edit',
					submitRoute: 'forecastCapacity',
					exitRoute: 'forecastCapacity'
				} as EditFormRouteMeta,
				props: true
			},
			{
				path: 'edit/:subJobId/sales',
				name: 'subJobEditSales',
				component: () => import('@/components/subJob/SubJobEdit.vue'),
				meta: {
					formMode: 'Edit',
					submitRoute: 'salesForecast',
					exitRoute: 'salesForecast'
				} as EditFormRouteMeta,
				props: true
			},
			{
				path: 'edit:/:subJobId/scrApprove/:scrId',
				name: 'subJobEditScr',
				component: () => import('@/components/subJob/SubJobEdit.vue'),
				meta: {
					formMode: 'Edit',
					submitRoute: 'subJobEdit',
					exitRoute: 'jobEdit',
					approveOnEnter: true
				} as EditFormRouteMeta,
				props: true
			},
			{
				path: 'edit/:subJobId',
				name: 'subJobEdit',
				component: () => import('@/components/subJob/SubJobEdit.vue'),
				meta: {
					formMode: 'Edit',
					submitRoute: 'subJobEdit',
					exitRoute: 'jobEdit'
				} as EditFormRouteMeta,
				props: true
			},
			{
				path: ':subJobId/quoteAdd',
				name: 'quoteAdd',
				component: () => import('@/views/QuoteEditView.vue'),
				props: true,
				meta: {
					formMode: 'Add',
					submitRoute: 'quoteEdit', // Stay on quote after save
					exitRoute: 'subJobEdit' // Exit returns to parent
				} as EditFormRouteMeta,
				beforeEnter: (
					to: RouteLocationNormalized,
					from: RouteLocationNormalized,
					next: NavigationGuardNext
				) => {
					const authStore = useAuthStore()
					// Require Quote.Add permissions to access this route
					if (authStore.getAccessForModuleAndRole('Quote', 'Add').value >= AccessLevel.Basic) {
						next()
					} else {
						// Redirect to parent SubJob if no Quote.Add permissions
						next({ name: 'subJobEdit', params: { subJobId: to.params.subJobId } })
					}
				}
			}
		]
	},
	{
		path: '/quotes',
		name: 'quotes',
		component: () => import('@/views/QuotesView.vue'),
		meta: {
			linkLabel: 'Quotes',
			linkNavBar: () => {
				return useAuthStore().getAccessForModuleAndRole('Quote', 'Read').value >= AccessLevel.Basic
			},
			filters: {
				salesTeam: (salesTeam: number) => {
					return (
						useAuthStore().mySalesTeams.find((sp: ISalesperson) => sp.id === salesTeam) !== undefined ||
						useAuthStore().getAccessForModuleAndRole('Quote', 'Read').value >= AccessLevel.Full
					)
				},
				row: () => {
					return useAuthStore().getAccessForModuleAndRole('Quote', 'Read').value >= AccessLevel.Expanded
				}
			},
			linkNavOrder: 5
		} as LinkRouteMeta,
		children: [
			{
				path: 'add',
				name: 'quoteAddStandalone',
				component: () => import('@/views/QuoteEditView.vue'),
				props: true,
				meta: {
					formMode: 'Add',
					submitRoute: 'quoteEdit', // Stay on quote after save
					exitRoute: 'quotes' // Return to quotes dashboard on cancel
				} as EditFormRouteMeta
			},
			{
				path: 'edit/:quoteId',
				name: 'quoteEdit',
				component: () => import('@/views/QuoteEditView.vue'),
				props: true,
				meta: {
					formMode: 'Edit',
					submitRoute: 'quoteEdit', // Stay on quote after save
					exitRoute: 'subJobEdit' // Returns to parent on exit
				} as EditFormRouteMeta
			},
			{
				path: 'duplicate/:quoteId',
				name: 'quoteDuplicate',
				component: () => import('@/views/QuoteEditView.vue'),
				props: (route: { params: { quoteId: string } }) => ({
					quoteId: '0', // New quote
					sourceQuoteId: route.params.quoteId // Source to duplicate
				}),
				meta: {
					formMode: 'Add',
					submitRoute: 'quoteEdit', // Stay on quote after save
					exitRoute: 'quoteEdit', // Return to source quote on cancel
					isDuplicateRoute: true // Flag to identify duplicate mode for bypassing dirty checks
				} as EditFormRouteMeta
			}
		]
	},
	{
		path: '/estimates',
		name: 'estimates',
		component: () => import('@/views/EstimatesView.vue'),
		meta: {
			linkLabel: 'Estimates',
			linkNavBar: () => {
				return useAuthStore().hasAnyDomainAccess('Estimate').value
			},
			linkNavOrder: 6
		} as LinkRouteMeta
	},
	{
		path: '',
		meta: {
			linkLabel: 'Forecast',
			linkNavBar: () => {
				return useAuthStore().hasAnyDomainAccess('Forecast').value
			},
			linkNavOrder: 7
		} as LinkRouteMeta,
		children: [
			{
				path: 'forecast',
				name: 'forecastIndex',
				component: () => import('@/components/forecast/ForecastIndex.vue'),
				meta: {
					linkLabel: 'Job Forecasts',
					filters: {
						salesTeam: (salesTeam: number) => {
							return (
								useAuthStore().mySalesTeams.find((sp: ISalesperson) => sp.id === salesTeam) !==
									undefined ||
								useAuthStore().getAccessForModuleAndRole('Forecast', 'Read').value >=
									AccessLevel.Expanded
							)
						},
						row: () => {
							return (
								useAuthStore().getAccessForModuleAndRole('Forecast', 'Read').value >= AccessLevel.Basic
							)
						}
					},
					defaultFilters: {
						dueDate: {
							from: new Date(new Date().setDate(new Date().getDate() - 30)),
							to: null
						}
					},
					addRoute: '',
					editRoute: 'forecastEdit'
				} as IndexFormRouteMeta
			},
			{
				path: 'edit/:subJobId',
				name: 'forecastEdit',
				component: () => import('@/components/forecast/ForecastEdit.vue'),
				props: true,
				meta: {
					formMode: 'Edit'
				} as EditFormRouteMeta
			},

			// {
			// 	path: 'add',
			// 	name: 'forecastAdd',
			// 	component: () => import('@/components/forecast/ForecastEdit.vue'),
			// 	meta: {
			// 		formMode: 'Add'
			// 	} as EditFormRouteMeta
			// },
			// {
			// 	path: 'ratio',
			// 	name: 'forecastRatio',
			// 	component: () => null,
			// 	meta: {
			// 		linkLabel: 'Ratio'
			// 	} as LinkRouteMeta
			// },
			{
				path: 'capacity',
				name: 'forecastCapacity',
				component: () => import('@/components/forecast/SiteCapacityIndex.vue'),
				meta: {
					linkLabel: 'Production Forecast',
					linkNavBar: () => {
						return (
							useAuthStore().getAccessForModuleAndRole('Forecast', 'Read').value >= AccessLevel.Expanded
						)
					}
				} as LinkRouteMeta
			},
			{
				path: 'sales-forecast',
				name: 'salesForecast',
				component: () => import('@/components/forecast/SalesForecast.vue'),
				meta: {
					linkLabel: 'Sales Forecast',
					filters: {
						salesTeam: (salesTeam: number) => {
							return (
								useAuthStore().mySalesTeams.find((sp: ISalesperson) => sp.id === salesTeam) !==
									undefined ||
								useAuthStore().getAccessForModuleAndRole('Forecast', 'Read').value >=
									AccessLevel.Expanded
							)
						},
						row: () => {
							return (
								useAuthStore().getAccessForModuleAndRole('Forecast', 'Read').value >= AccessLevel.Basic
							)
						}
					},
					addRoute: '',
					editRoute: ''
				} as IndexFormRouteMeta
			}
			// {
			// 	path: 'tables',
			// 	name: 'forecastTables',
			// 	meta: {
			// 		linkLabel: 'Tables'
			// 	} as LinkRouteMeta,
			// 	children: [
			// 		{
			// 			path: 'capacity',
			// 			name: 'forecastCapacity',
			// 			component: () => import('@/components/forecast/SiteCapacityIndex.vue'),
			// 			meta: {
			// 				linkLabel: 'Capacity'
			// 			} as LinkRouteMeta
			// 		},
			// 		{
			// 			path: 'laborcurve',
			// 			name: 'forecastLaborCurve',
			// 			component: () => null,
			// 			meta: {
			// 				linkLabel: 'Labor Curve'
			// 			} as LinkRouteMeta
			// 		}
			// 	]
			// }
			// {
			// 	path: 'sitecapacity',
			// 	name: 'siteCapacity',
			// 	component: () => import('@/components/forecast/SiteCapacity.vue')
			// }
		]
	},
	{
		path: '/theme',
		name: 'theme',
		component: () => import('@/views/ThemeView.vue'),
		children: [
			{
				path: 'background',
				name: 'themeBackground',
				component: () => import('@/components/theme/CardBackground.vue')
			}
		]
	},
	{
		path: '/report',
		name: 'report',
		component: () => import('@/components/AppReport.vue')
	},
	{
		path: '/richtext-test',
		name: 'richTextTest',
		component: () => import('@/components/common/richtext/TestRichText.vue')
	},
	{
		path: '/richtext-quote-test',
		name: 'richTextQuoteTest',
		component: () => import('@/components/common/richtext/TestQuoteRichText.vue')
	}
]

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes
})

const client = new VueNavigationClient(router)

const encodeParams = (params: RouteParams) => {
	for (const key in params) {
		if (Object.prototype.hasOwnProperty.call(params, key)) {
			if (params[key].constructor.name === 'Array') {
				params[key] = (params[key] as string[]).map(encodeURIComponent)
			} else {
				params[key] = encodeURIComponent(params[key] as string)
			}
		}
	}
}

router.beforeEach(async (to, from, next) => {
	// 404
	if (to.matched[0]?.path === '/:pathMatch(.*)*') {
		return next()
	}

	// Handle quote duplication flag - set before components mount
	const quoteStore = useQuoteStore()
	if (to.meta?.isDuplicateRoute === true) {
		// Set flag early to prevent dirty checks during component initialization
		quoteStore.isDuplicating = true
	} else if (from.meta?.isDuplicateRoute === true && to.meta?.isDuplicateRoute !== true) {
		// Clear flag when navigating away from duplicate route
		quoteStore.isDuplicating = false
	}

	const authStore = useAuthStore()
	// Login required
	const guarded = unguarded.every((path) => path !== to.path)
	if (guarded) {
		if (!authStore.initialized) {
			await authStore.initialize(client)
		}
		if (authStore.account) {
			return next()
		}
		try {
			await authStore.login()
			return next()
		} catch (err) {
			console.log('This dont look right', err)
			//TODO: We are probably going to want some sort of landing page here.
			//Perhaps a dedicated security page detailing "You must login to access Nuevo Peco"
			return next(false)
		}
	}

	if (to.params) {
		encodeParams(to.params)
	}
	next()
})

export default router
